import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Briefcase, 
  Users, 
  UserCog, 
  DollarSign, 
  FileText, 
  Calendar, 
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  Scale,
  MessageSquare,
  Gavel
} from 'lucide-react';
import { cn } from '../../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Sidebar = ({ collapsed, setCollapsed }) => {
  const { t, direction, language } = useLanguage();
  const { logout, user, canManageEmployees, token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      if (token) {
        try {
          const res = await axios.get(`${API_URL}/api/messages/unread/count`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUnreadMessages(res.data.count);
        } catch (e) {
          console.log('Failed to fetch unread count');
        }
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: t('dashboard') },
    { path: '/cases', icon: Briefcase, label: t('cases') },
    // Only admin can see clients list
    ...(isAdmin ? [{ path: '/clients', icon: Users, label: t('clients') }] : []),
    // Only admin can see lawyers list
    ...(isAdmin ? [{ path: '/lawyers', icon: Gavel, label: language === 'ar' ? 'المحامين' : 'Lawyers' }] : []),
    // Only admin can see employees
    ...(canManageEmployees ? [{ path: '/employees', icon: UserCog, label: t('employees') }] : []),
    // Only admin can see full finance
    ...(isAdmin ? [{ path: '/finance', icon: DollarSign, label: t('finance') }] : []),
    { path: '/documents', icon: FileText, label: t('documents') },
    { path: '/calendar', icon: Calendar, label: t('calendar') },
    { path: '/messages', icon: MessageSquare, label: language === 'ar' ? 'الرسائل' : 'Messages', badge: unreadMessages },
    { path: '/settings', icon: Settings, label: t('settings') },
  ];

  const CollapseIcon = direction === 'rtl' 
    ? (collapsed ? ChevronLeft : ChevronRight)
    : (collapsed ? ChevronRight : ChevronLeft);

  return (
    <aside 
      className={cn(
        'sidebar flex flex-col',
        direction === 'rtl' ? 'sidebar-rtl' : 'sidebar-ltr',
        collapsed && 'sidebar-collapsed'
      )}
      data-testid="sidebar"
    >
      {/* Logo Section */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className={cn('flex items-center gap-3', collapsed && 'justify-center w-full')}>
          <div className="w-10 h-10 rounded-full overflow-hidden bg-white flex items-center justify-center">
            <img src="/logo.png" alt="التمكين" className="w-9 h-9 object-contain" />
          </div>
          {!collapsed && (
            <span className="font-semibold text-lg tracking-tight">
              {language === 'ar' ? 'عملاء التمكين' : 'Tamkeen Clients'}
            </span>
          )}
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            'p-1.5 rounded-sm hover:bg-white/10 transition-colors',
            collapsed && 'absolute -right-3 top-6 bg-[hsl(var(--sidebar-background))] border border-white/10',
            direction === 'rtl' && collapsed && '-left-3 right-auto'
          )}
          data-testid="sidebar-toggle"
        >
          <CollapseIcon className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin">
        <ul className="space-y-1 px-2">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) => cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-200 relative',
                  'hover:bg-white/10',
                  isActive && 'bg-[hsl(var(--sidebar-accent))] text-white',
                  collapsed && 'justify-center'
                )}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                {!collapsed && (
                  <span className="text-sm font-medium">{item.label}</span>
                )}
                {item.badge > 0 && (
                  <span className={cn(
                    'bg-red-500 text-white text-xs rounded-full flex items-center justify-center',
                    collapsed ? 'absolute -top-1 -right-1 w-4 h-4' : 'ml-auto w-5 h-5'
                  )}>
                    {item.badge}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-white/10">
        {!collapsed && (
          <div className="mb-3 px-3">
            <p className="text-sm font-medium truncate">{user?.full_name}</p>
            <p className="text-xs text-white/60 capitalize">{user?.role}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={cn(
            'flex items-center gap-3 w-full px-3 py-2.5 rounded-sm',
            'text-red-400 hover:bg-red-500/20 transition-colors',
            collapsed && 'justify-center'
          )}
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          {!collapsed && <span className="text-sm font-medium">{t('logout')}</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
